/**
 * 압력 입력 단위 전환.
 *
 * **계산은 언제나 kPa 로 한다.** 표준 식의 상수(11.78, 190.5, 277.8, 16.67 …)가
 * 전부 SI kPa/Pa 기준이라, 계산 계층은 단위를 알 필요가 없어야 한다.
 * 이 모듈은 화면 입력값을 kPa 로 바꿔주는 얇은 껍데기다.
 *
 * ── 가장 위험한 실수 ────────────────────────────────────────
 * 단위만 바꾸고 숫자를 그대로 두면 **의미가 조용히 달라진다.**
 * 517 kPag 로 넣어둔 값을 barg 로 바꾸면 517 barg(= 51,700 kPa)가 되어
 * 계산은 멀쩡히 돌아가지만 결과가 100배 틀린다.
 * 그래서 `convertInput()` 으로 **숫자도 함께 환산**한다. 물리량은 그대로 두고
 * 표기만 바꾸는 것이 사용자가 기대하는 동작이다.
 *
 * 게이지압/절대압 구분은 이 모듈이 다루지 않는다. 그건 항목마다 정해져
 * 있고(설정압은 게이지, 기체 배압은 절대), 단위 배수와는 무관하다.
 */

export type PressureUnit = "kPa" | "bar" | "kgfcm2" | "MPa" | "psi";

interface UnitSpec {
  /** 단위 자체의 이름 */
  readonly label: string;
  /** 게이지압 표기 */
  readonly gauge: string;
  /** 절대압 표기 */
  readonly abs: string;
  /** 이 단위 1 이 몇 kPa 인가 */
  readonly toKpa: number;
  /**
   * 환산 결과를 표시할 최대 소수 자리.
   * kPa 로 갔다 돌아와도 값이 흔들리지 않을 만큼은 남겨야 한다
   * (예: psi 는 자리가 부족하면 왕복에서 0.1 kPa 씩 어긋난다).
   */
  readonly decimals: number;
}

/**
 * 환산 계수.
 *   1 bar      = 100 kPa           (정의)
 *   1 kgf/cm²  = 98.0665 kPa       (표준중력 9.80665 m/s² 기준, 정의값)
 *   1 MPa      = 1000 kPa          (정의)
 *   1 psi      = 6.894757293168361 kPa  (1 lbf/in², 정의값)
 */
export const PRESSURE_UNITS: Record<PressureUnit, UnitSpec> = {
  kPa: { label: "kPa", gauge: "kPag", abs: "kPa abs", toKpa: 1, decimals: 2 },
  bar: { label: "bar", gauge: "barg", abs: "bar abs", toKpa: 100, decimals: 5 },
  kgfcm2: {
    label: "kg/cm²",
    gauge: "kg/cm²g",
    abs: "kg/cm² abs",
    toKpa: 98.0665,
    decimals: 5,
  },
  MPa: { label: "MPa", gauge: "MPag", abs: "MPa abs", toKpa: 1000, decimals: 6 },
  psi: { label: "psi", gauge: "psig", abs: "psia", toKpa: 6.894757293168361, decimals: 3 },
};

export const PRESSURE_UNIT_KEYS = Object.keys(PRESSURE_UNITS) as PressureUnit[];

/** 표준 대기압 (kPa). 게이지압 ↔ 절대압 변환에 쓴다. */
export const ATMOSPHERIC_KPA = 101.325;

/** 사용자가 고른 단위의 값 → kPa */
export function toKpa(value: number, unit: PressureUnit): number {
  return value * PRESSURE_UNITS[unit].toKpa;
}

/** kPa → 사용자가 고른 단위의 값 */
export function fromKpa(kpa: number, unit: PressureUnit): number {
  return kpa / PRESSURE_UNITS[unit].toKpa;
}

/** 게이지압 표기. 예: "kPag", "kg/cm²g" */
export function gaugeLabel(unit: PressureUnit): string {
  return PRESSURE_UNITS[unit].gauge;
}

/** 절대압 표기. 예: "kPa abs", "psia" */
export function absLabel(unit: PressureUnit): string {
  return PRESSURE_UNITS[unit].abs;
}

/** 꼬리의 0 을 정리해 사람이 읽기 좋은 문자열로. */
function trim(v: number, decimals: number): string {
  if (!Number.isFinite(v)) return "";
  const s = v.toFixed(decimals);
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}

/** kPa 값을 해당 단위의 표시 문자열로. */
export function formatFromKpa(kpa: number, unit: PressureUnit): string {
  return trim(fromKpa(kpa, unit), PRESSURE_UNITS[unit].decimals);
}

/**
 * 단위를 바꿀 때 입력 문자열을 새 단위 값으로 환산한다.
 *
 * **물리량은 그대로 두고 표기만 바꾼다.** 517 kPag → barg 로 바꾸면 "5.17".
 * 숫자로 읽을 수 없는 문자열(빈칸, 입력 중인 "-", "0." 등)은 건드리지 않는다 —
 * 사용자가 타이핑하던 중간 상태를 지우면 안 된다.
 */
export function convertInput(
  text: string,
  from: PressureUnit,
  to: PressureUnit,
): string {
  if (from === to) return text;
  const v = Number.parseFloat(text);
  if (!Number.isFinite(v)) return text;
  return trim(
    (v * PRESSURE_UNITS[from].toKpa) / PRESSURE_UNITS[to].toKpa,
    PRESSURE_UNITS[to].decimals,
  );
}

/** 저장 파일에서 읽은 값이 올바른 단위인지 확인한다. */
export function isPressureUnit(v: unknown): v is PressureUnit {
  return typeof v === "string" && v in PRESSURE_UNITS;
}
